import { useState, useEffect, useMemo } from "react";
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import i18n from "../i18n/i18n";
import finalImage from "../assets/images/gallery/photo_2025-09-20_21-06-12.jpg";

const FinalMessage = () => {
  const [displayedMessage, setDisplayedMessage] = useState("");
  const [showPicture, setShowPicture] = useState(false);
  const [displayedTitle, setDisplayedTitle] = useState("");
  const [displayedSubtitle, setDisplayedSubtitle] = useState("");
  const [currentSubtitleIndex, setCurrentSubtitleIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingPause, setTypingPause] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language);

  // Handle language changes without resetting the animation
  useEffect(() => {
    const handleLanguageChange = () => {
      if (i18n.language !== currentLang) {
        setCurrentLang(i18n.language);
        // Update the displayed text to the new language without resetting
        if (displayedMessage) {
          const newMessage = i18n.t('initial_message');
          setDisplayedMessage(newMessage.slice(0, displayedMessage.length));
        }
        if (displayedTitle) {
          const newTitle = i18n.t('happy_birthday');
          setDisplayedTitle(newTitle.slice(0, displayedTitle.length));
        }
      }
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [currentLang, displayedMessage, displayedTitle]);

  // Memoize sweetWords to update only when language changes
  const sweetWords = useMemo(() => [
    i18n.t('love_1'),
    i18n.t('love_2'),
    i18n.t('love_3'),
    i18n.t('love_4'),
    i18n.t('love_5'),
    i18n.t('love_6'),
    i18n.t('love_7'),
    i18n.t('love_8'),
    i18n.t('love_9'),
    i18n.t('love_10'),
    i18n.t('love_11'),
    i18n.t('love_12'),
    i18n.t('love_13'),
    i18n.t('love_14'),
    i18n.t('love_15'),
    i18n.t('love_16'),
    i18n.t('love_17'),
    i18n.t('love_18'),
    i18n.t('love_19'),
    i18n.t('love_20'),
    i18n.t('love_21'),
    i18n.t('love_22'),
    i18n.t('love_23'),
    i18n.t('love_24'),
    i18n.t('love_25'),
    i18n.t('love_26'),
    i18n.t('love_27'),
    i18n.t('love_28'),
    i18n.t('love_29'),
    i18n.t('love_30'),
    i18n.t('love_31'),
    i18n.t('love_32'),
    i18n.t('love_33'),
    i18n.t('love_34'),
    i18n.t('love_35'),
    i18n.t('love_36'),
    i18n.t('love_37'),
    i18n.t('love_38'),
    i18n.t('love_39'),
    i18n.t('love_40'),
    i18n.t('love_41'),
    i18n.t('love_42'),
    i18n.t('love_43'),
    i18n.t('love_44'),
    i18n.t('love_45'),
    i18n.t('love_46'),
    i18n.t('love_47'),
    i18n.t('love_48'),
    i18n.t('love_49'),
    i18n.t('love_50')
  ], [currentLang]);

  const currentSubtitle = sweetWords[currentSubtitleIndex];
  const initialMessage = i18n.t('initial_message');
  const titleText = i18n.t('happy_birthday');
  const pictureUrl = finalImage;

  // Typing animation for initial message
  useEffect(() => {
    if (displayedMessage.length < initialMessage.length && !showPicture) {
      const timer = setTimeout(() => {
        setDisplayedMessage(initialMessage.slice(0, displayedMessage.length + 1));
      }, 50);
      return () => clearTimeout(timer);
    } else if (displayedMessage.length === initialMessage.length && !showPicture) {
      const timer = setTimeout(() => {
        setShowPicture(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [displayedMessage, showPicture, initialMessage]);

  // Typing animation for title
  useEffect(() => {
    if (showPicture && displayedTitle.length < titleText.length) {
      const timer = setTimeout(() => {
        setDisplayedTitle(titleText.slice(0, displayedTitle.length + 1));
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [displayedTitle, showPicture, titleText]);

  // Typing animation for subtitle
  useEffect(() => {
    if (!showPicture || displayedTitle.length !== titleText.length) return;

    if (typingPause) {
      const timer = setTimeout(() => {
        setTypingPause(false);
        setIsDeleting(true);
      }, 2000); // Pause for 2 seconds before starting to delete
      return () => clearTimeout(timer);
    }

    if (isDeleting) {
      if (displayedSubtitle.length > 0) {
        const timer = setTimeout(() => {
          setDisplayedSubtitle(displayedSubtitle.slice(0, -1));
        }, 30); // Faster deletion speed
        return () => clearTimeout(timer);
      } else {
        // Move to next sweet word
        setCurrentSubtitleIndex((prevIndex) => (prevIndex + 1) % sweetWords.length);
        setIsDeleting(false);
      }
    } else {
      if (displayedSubtitle.length < currentSubtitle.length) {
        const timer = setTimeout(() => {
          setDisplayedSubtitle(currentSubtitle.slice(0, displayedSubtitle.length + 1));
        }, 100); // Typing speed
        return () => clearTimeout(timer);
      } else {
        // Finished typing current word, pause before deleting
        setTypingPause(true);
      }
    }
  }, [displayedSubtitle, showPicture, displayedTitle, currentSubtitle, isDeleting, typingPause, sweetWords, titleText]);

  const { width, height } = useWindowSize();

  return (
    <div className="min-h-screen bg-background overflow-hidden relative">
      
      {/* Confetti effect */}
      {showPicture && (
        <Confetti
          width={width}
          height={height}
          recycle={true}
          numberOfPieces={200}
          gravity={0.2}
          initialVelocityY={15}
        />
      )}

      {/* Initial Message */}
      {!showPicture && (
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="max-w-2xl text-center">
            <p className="text-2xl md:text-3xl font-semibold text-foreground leading-relaxed">
              {displayedMessage}
              {displayedMessage.length < initialMessage.length && (
                <span className="animate-pulse">|</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Picture with Title and Subtitle */}
      {showPicture && (
        <div className="min-h-screen flex flex-col items-center justify-center p-6">
          <div className="flex flex-col items-center gap-8 animate-fade-in">
            {/* Picture */}
            <div className="relative">
              <img
                src={pictureUrl}
                alt="Birthday"
                className="w-80 h-96 md:w-96 md:h-[500px] object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </div>

            {/* Title */}
            <div className="text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-4">
                {displayedTitle}
                {displayedTitle.length < titleText.length && (
                  <span className="animate-pulse">|</span>
                )}
              </h1>

              {/* Subtitle */}
              <p className="text-2xl md:text-3xl font-semibold text-foreground">
                {displayedSubtitle}
                {displayedSubtitle.length < currentSubtitle.length && (
                  <span className="animate-pulse">|</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default FinalMessage;